import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { cacheGet, cacheSet } from "../lib/offline";
import { MagnifyingGlass, X, ListChecks, PlusCircle, ArrowLeft, PaperPlaneTilt, CheckCircle, GearSix, Vault, FilePdf } from "@phosphor-icons/react";
import SOPStudioPage from "./SOPStudioPage";
import BrandStudioPage from "./BrandStudioPage";
import { toast } from "sonner";
import { apiError } from "../lib/api";
import { DepartmentPicker, useDepartments } from "../components/DepartmentPicker";

export default function SOPVaultPage() {
  const { user } = useAuth();
  const departments = useDepartments();
  const [venues, setVenues] = useState([]);
  const [categories, setCategories] = useState([]);
  const [sops, setSops] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");
  const [deptFilter, setDeptFilter] = useState("");
  const [venueFilter, setVenueFilter] = useState("");
  const [active, setActive] = useState(null);
  const [scopeDraft, setScopeDraft] = useState(null);
  const [showStudio, setShowStudio] = useState(false);
  const [showBrand, setShowBrand] = useState(false);
  const [showCats, setShowCats] = useState(false);
  const [catDraft, setCatDraft] = useState([]);
  const [newCatName, setNewCatName] = useState("");
  const isTopAdmin = ["admin", "gm"].includes(user.role) || user.is_master_operator;
  const canCreate = ["admin", "gm", "manager"].includes(user.role);
  const isStaff = ["staff", "supervisor"].includes(user.role);
  const activeDepts = departments.filter((d) => d.active !== false);
  const deptName = (id) => departments.find((d) => d.id === id)?.name;
  const venueName = (id) => venues.find((v) => v.id === id)?.name;
  const scopeLabel = (s) => {
    const vn = (s.venue_ids || []).map(venueName).filter(Boolean);
    const dn = (s.department_ids || []).map(deptName).filter(Boolean);
    if (vn.length) return vn.join(", ");
    if (dn.length) return dn.join(", ");
    return "Brand-wide";
  };
  const [pending, setPending] = useState([]);
  const [showDraft, setShowDraft] = useState(false);
  const [draft, setDraft] = useState({ title: "", category: "Service", summary: "", steps: "", department_ids: [] });

  useEffect(() => {
    api.get("/sop-config").then((r) => {
      setCategories(r.data.categories || []);
      setDraft((d) => ({ ...d, category: r.data.categories?.[0] || "Service" }));
    }).catch(() => {});
    api.get("/venues").then((r) => setVenues(r.data)).catch(() => {});
  }, []);

  const loadPending = () => {
    if (canCreate) api.get("/sops", { params: { status: "pending_review" } }).then((r) => setPending(r.data)).catch(() => {});
  };

  const submitDraft = async () => {
    if (!draft.title.trim() || !draft.steps.trim()) return toast.error("Title and steps are required");
    try {
      await api.post("/sops", { title: draft.title, category: draft.category, summary: draft.summary,
        steps: draft.steps.split("\n").map((s) => s.trim()).filter(Boolean),
        department_ids: draft.department_ids, published: false });
      toast.success("SOP draft submitted — your manager has been notified for review");
      setShowDraft(false);
      setDraft({ title: "", category: "Service", summary: "", steps: "", department_ids: [] });
    } catch (e) { toast.error(apiError(e)); }
  };

  const reviewSop = async (id, action) => {
    try {
      await api.put(`/sops/${id}/review`, { action, note: "" });
      toast.success(action === "approve" ? "SOP approved & published" : "SOP sent back to author");
      loadPending(); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const load = useCallback(() => {
    api.get("/sops", { params: { q, category: cat === "All" ? "" : cat, department_id: deptFilter, venue_id: venueFilter } })
      .then((r) => {
        setSops(r.data);
        if (!q && cat === "All" && !deptFilter && !venueFilter) cacheSet("sops:all", r.data);
      })
      .catch((e) => {
        if (!e.response) {
          const all = cacheGet("sops:all") || [];
          const ql = q.toLowerCase();
          setSops(all.filter((s) =>
            (cat === "All" || s.category === cat) &&
            (!ql || s.title.toLowerCase().includes(ql) || s.summary.toLowerCase().includes(ql) ||
              s.tags?.some((t) => t.toLowerCase().includes(ql)))));
        }
      });
  }, [q, cat, deptFilter, venueFilter]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);
  useEffect(() => { loadPending(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const saveScope = async () => {
    try {
      const { data } = await api.put(`/sops/${active.id}/scope`, scopeDraft);
      setActive(data); setScopeDraft(null); load();
      toast.success("SOP scope updated");
    } catch (e) { toast.error(apiError(e)); }
  };
  const saveCats = async () => {
    try {
      const { data } = await api.put("/sop-config", { categories: catDraft });
      setCategories(data.categories); setShowCats(false);
      toast.success("Categories saved");
    } catch (e) { toast.error(apiError(e)); }
  };

  if (showBrand) {
    return (
      <div className="space-y-6 rise-in" data-testid="brand-media-embedded">
        <button data-testid="brand-media-back" onClick={() => { setShowBrand(false); load(); }}
          className="h-11 px-5 rounded-full border border-slate-200 text-sm font-bold text-slate-600 flex items-center gap-2 tap-press hover:border-slate-900">
          <ArrowLeft size={16} /> Back to SOP Vault
        </button>
        <BrandStudioPage />
      </div>
    );
  }

  if (showStudio) {
    return (
      <div className="space-y-6 rise-in" data-testid="sop-studio-embedded">
        <button data-testid="sop-studio-back" onClick={() => { setShowStudio(false); load(); }}
          className="h-11 px-5 rounded-full border border-slate-200 text-sm font-bold text-slate-600 flex items-center gap-2 tap-press hover:border-slate-900">
          <ArrowLeft size={16} /> Back to SOP Vault
        </button>
        <SOPStudioPage />
      </div>
    );
  }

  return (
    <div className="space-y-8 rise-in" data-testid="sop-vault-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="text-sm uppercase tracking-[0.2em] font-bold text-slate-400">Unified Knowledge Base</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight mt-2">SOP Vault</h1>
        </div>
        <div className="flex gap-2 flex-wrap shrink-0">
          {user.role === "admin" && (
            <button data-testid="brand-media-button" onClick={() => setShowBrand(true)}
              className="h-12 px-5 rounded-full border border-slate-300 text-slate-700 font-bold text-sm flex items-center gap-2 tap-press hover:border-slate-900">
              <Vault size={18} /> Brand Media Studio
            </button>
          )}
          {isTopAdmin && (
            <button data-testid="sop-categories-button" onClick={() => { setCatDraft([...categories]); setShowCats(true); }}
              className="h-12 px-4 rounded-full border border-slate-300 text-slate-700 font-bold text-sm flex items-center gap-2 tap-press hover:border-slate-900">
              <GearSix size={18} /> Categories
            </button>
          )}
          {canCreate && (
            <button data-testid="sop-add-button" onClick={() => setShowStudio(true)}
              className="h-12 px-6 rounded-full bg-slate-900 text-white font-bold text-sm flex items-center gap-2 tap-press hover:bg-slate-800">
              <PlusCircle size={18} /> Add SOP
            </button>
          )}
          {isStaff && (
            <button data-testid="sop-draft-button" onClick={() => setShowDraft(true)}
              className="h-12 px-6 rounded-full border border-slate-300 text-slate-700 font-bold text-sm flex items-center gap-2 tap-press hover:border-slate-900">
              <PaperPlaneTilt size={18} /> Submit SOP draft
            </button>
          )}
        </div>
      </div>

      {isTopAdmin && (
        <div className="space-y-2" data-testid="sop-scope-switcher">
          <div className="flex flex-wrap gap-2">
            <button data-testid="sop-venue-all" onClick={() => setVenueFilter("")}
              className={`h-9 px-4 rounded-full text-xs font-bold tap-press border ${!venueFilter ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-500 border-slate-200 hover:border-slate-900"}`}>
              All venues
            </button>
            {venues.map((v) => (
              <button key={v.id} data-testid={`sop-venue-switch-${v.id}`} onClick={() => setVenueFilter(venueFilter === v.id ? "" : v.id)}
                className={`h-9 px-4 rounded-full text-xs font-bold tap-press border ${venueFilter === v.id ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-500 border-slate-200 hover:border-slate-900"}`}>
                {v.name}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            <button data-testid="sop-dept-all" onClick={() => setDeptFilter("")}
              className={`h-9 px-4 rounded-full text-xs font-bold tap-press border ${!deptFilter ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-500 border-slate-200 hover:border-slate-900"}`}>
              All departments
            </button>
            {activeDepts.map((d) => (
              <button key={d.id} data-testid={`sop-dept-switch-${d.id}`} onClick={() => setDeptFilter(deptFilter === d.id ? "" : d.id)}
                className={`h-9 px-4 rounded-full text-xs font-bold tap-press border ${deptFilter === d.id ? "bg-sky-600 text-white border-sky-600" : "bg-white text-slate-500 border-slate-200 hover:border-slate-900"}`}>
                {d.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {canCreate && pending.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6" data-testid="sop-pending-queue">
          <h2 className="font-heading text-lg font-bold text-amber-800 mb-4">{pending.length} SOP draft{pending.length > 1 ? "s" : ""} awaiting your review</h2>
          <div className="space-y-3">
            {pending.map((s) => (
              <div key={s.id} data-testid={`sop-pending-${s.id}`} className="bg-white border border-amber-100 rounded-xl p-4 flex flex-wrap items-center gap-3">
                <div className="flex-1 min-w-[200px]">
                  <p className="font-bold text-sm">{s.title}</p>
                  <p className="text-xs text-slate-400 mt-0.5">by {s.created_by} · {s.category} · {(s.steps || []).length} steps</p>
                </div>
                <button data-testid={`sop-approve-${s.id}`} onClick={() => reviewSop(s.id, "approve")}
                  className="h-10 px-4 rounded-full bg-emerald-600 text-white text-xs font-bold flex items-center gap-1.5 tap-press hover:bg-emerald-700">
                  <CheckCircle size={14} /> Approve & publish
                </button>
                <button data-testid={`sop-reject-${s.id}`} onClick={() => reviewSop(s.id, "reject")}
                  className="h-10 px-4 rounded-full border border-red-200 text-red-600 text-xs font-bold tap-press hover:bg-red-50">
                  Send back
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {showDraft && (
        <div className="fixed inset-0 z-[1000] bg-slate-950/50 flex items-center justify-center p-4 sm:p-6" onClick={() => setShowDraft(false)}>
          <div data-testid="sop-draft-modal" onClick={(e) => e.stopPropagation()}
            className="bg-white w-full sm:max-w-lg max-h-[90vh] overflow-y-auto rounded-3xl p-6 sm:p-8 rise-in">
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-heading text-2xl font-bold">Submit an SOP draft</h2>
              <button onClick={() => setShowDraft(false)} className="p-2 rounded-full hover:bg-slate-100 tap-press"><X size={22} /></button>
            </div>
            <p className="text-sm text-slate-500 mb-5">Your draft goes to your department manager for review before it's published.</p>
            <div className="space-y-4">
              <input data-testid="sop-draft-title" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                placeholder="SOP title" className="w-full h-12 px-4 rounded-xl border border-slate-200" />
              <select data-testid="sop-draft-category" value={draft.category} onChange={(e) => setDraft({ ...draft, category: e.target.value })}
                className="w-full h-12 px-4 rounded-xl border border-slate-200 bg-white font-semibold text-sm">
                {categories.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <input data-testid="sop-draft-summary" value={draft.summary} onChange={(e) => setDraft({ ...draft, summary: e.target.value })}
                placeholder="One-line summary (optional)" className="w-full h-12 px-4 rounded-xl border border-slate-200" />
              <textarea data-testid="sop-draft-steps" value={draft.steps} onChange={(e) => setDraft({ ...draft, steps: e.target.value })}
                rows={6} placeholder={"One step per line:\nGreet the guest within 30 seconds\nConfirm the booking name…"}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm" />
              <DepartmentPicker label="For department(s)" value={draft.department_ids} testId="sop-draft-dept-picker"
                onChange={(ids) => setDraft({ ...draft, department_ids: ids })} />
              <button data-testid="sop-draft-submit" onClick={submitDraft}
                className="w-full h-14 rounded-full bg-slate-900 text-white font-heading font-bold tap-press hover:bg-slate-800">
                Submit for manager review
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="sticky top-0 md:top-4 z-20 bg-[#F8FAFC]/90 backdrop-blur-md py-2 -my-2 space-y-4">
        <div className="relative">
          <MagnifyingGlass size={22} className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="sop-search-input" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search SOPs, recipes, tags…"
            className="w-full h-16 pl-14 pr-5 rounded-2xl border border-slate-200 bg-white text-lg focus:outline-none focus:ring-2 focus:ring-slate-900" />
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1 items-center">
          {["All", ...categories].map((c) => (
            <button key={c} data-testid={`sop-category-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} onClick={() => setCat(c)}
              className={`h-11 px-5 rounded-full text-sm font-bold whitespace-nowrap tap-press border ${
                cat === c ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>
              {c}
            </button>
          ))}
          {!isTopAdmin && (
            <select data-testid="sop-dept-filter" value={deptFilter} onChange={(e) => setDeptFilter(e.target.value)}
              className="h-11 px-4 rounded-full border border-slate-200 bg-white font-bold text-sm shrink-0">
              <option value="">All my departments</option>
              {activeDepts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          )}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="sop-grid">
        {sops.map((s) => (
          <button key={s.id} data-testid={`sop-card-${s.id}`} onClick={() => setActive(s)}
            className="text-left bg-white border border-slate-200 rounded-2xl overflow-hidden card-lift tap-press">
            {s.image_url && <img src={s.image_url} alt={s.title} className="h-40 w-full object-cover" />}
            <div className="p-5">
              <span className="text-[11px] uppercase tracking-[0.15em] font-bold text-emerald-600">{s.category}</span>
              <h3 className="font-heading font-bold text-lg mt-1 leading-snug">{s.title}</h3>
              <p className="text-sm text-slate-500 mt-2 line-clamp-2">{s.summary}</p>
              <div className="flex items-center gap-2 mt-4 text-xs text-slate-400 font-semibold flex-wrap">
                <ListChecks size={16} /> {s.steps.length} steps · {s.quiz?.length || 0} quiz Qs
                <span data-testid={`sop-scope-${s.id}`} className="text-[10px] font-bold uppercase tracking-wider bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">{scopeLabel(s)}</span>
              </div>
            </div>
          </button>
        ))}
        {sops.length === 0 && <p className="text-slate-400 col-span-full">No SOPs match your search.</p>}
      </div>

      {active && (
        <div className="fixed inset-0 z-[1000] bg-slate-950/50 flex items-center justify-center p-4 sm:p-6" onClick={() => setActive(null)}>
          <div data-testid="sop-detail-modal" onClick={(e) => e.stopPropagation()}
            className="bg-white w-full sm:max-w-2xl max-h-[90vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl rise-in">
            {active.image_url && <img src={active.image_url} alt={active.title} className="h-52 w-full object-cover" />}
            <div className="p-6 sm:p-8">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <span className="text-[11px] uppercase tracking-[0.15em] font-bold text-emerald-600">{active.category}</span>
                  <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mt-1">{active.title}</h2>
                </div>
                <button data-testid="sop-detail-close" onClick={() => setActive(null)} className="p-2 rounded-full hover:bg-slate-100 tap-press"><X size={24} /></button>
              </div>
              <p className="text-slate-600 mt-3 leading-relaxed">{active.summary}</p>
              {active.video_url && (
                <video data-testid="sop-video-embed" src={active.video_url} controls className="w-full rounded-xl mt-4 max-h-72 bg-black" />
              )}
              {active.attachment_url && (
                <a data-testid="sop-attachment-link" href={active.attachment_url} target="_blank" rel="noreferrer"
                  className="flex items-center gap-2 mt-4 text-sm font-bold text-sky-700 bg-sky-50 border border-sky-100 rounded-xl px-4 py-3 hover:bg-sky-100">
                  <FilePdf size={20} /> {active.attachment_name || "View attachment"}
                </a>
              )}
              {isTopAdmin && (
                <div className="mt-4 bg-slate-50 border border-slate-100 rounded-xl p-4" data-testid="sop-scope-editor">
                  {!scopeDraft ? (
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <p className="text-xs font-bold text-slate-500">Scope: <span className="text-slate-800">{scopeLabel(active)}</span> · Category: <span className="text-slate-800">{active.category}</span></p>
                      <button data-testid="sop-rescope-button"
                        onClick={() => setScopeDraft({ category: active.category, venue_ids: active.venue_ids || [], department_ids: active.department_ids || [] })}
                        className="h-9 px-4 rounded-full border border-slate-300 text-xs font-bold tap-press hover:border-slate-900">
                        Re-scope / re-categorize
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <select data-testid="scope-category-select" value={scopeDraft.category}
                        onChange={(e) => setScopeDraft({ ...scopeDraft, category: e.target.value })}
                        className="w-full h-11 px-4 rounded-xl border border-slate-200 bg-white font-semibold text-sm">
                        {[...new Set([scopeDraft.category, ...categories])].map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                      <div className="flex flex-wrap gap-2">
                        {venues.map((v) => (
                          <button key={v.id} type="button" data-testid={`scope-venue-${v.id}`}
                            onClick={() => setScopeDraft({ ...scopeDraft, venue_ids: scopeDraft.venue_ids.includes(v.id) ? scopeDraft.venue_ids.filter((x) => x !== v.id) : [...scopeDraft.venue_ids, v.id] })}
                            className={`h-8 px-3 rounded-full text-xs font-bold tap-press border ${scopeDraft.venue_ids.includes(v.id) ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-500 border-slate-200"}`}>
                            {v.name}
                          </button>
                        ))}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {activeDepts.map((d) => (
                          <button key={d.id} type="button" data-testid={`scope-dept-${d.id}`}
                            onClick={() => setScopeDraft({ ...scopeDraft, department_ids: scopeDraft.department_ids.includes(d.id) ? scopeDraft.department_ids.filter((x) => x !== d.id) : [...scopeDraft.department_ids, d.id] })}
                            className={`h-8 px-3 rounded-full text-xs font-bold tap-press border ${scopeDraft.department_ids.includes(d.id) ? "bg-sky-600 text-white border-sky-600" : "bg-white text-slate-500 border-slate-200"}`}>
                            {d.name}
                          </button>
                        ))}
                      </div>
                      <p className="text-[11px] text-slate-400">No venue and no department selected = Brand-wide.</p>
                      <div className="flex gap-2">
                        <button data-testid="scope-save-button" onClick={saveScope}
                          className="h-10 px-5 rounded-full bg-slate-900 text-white text-xs font-bold tap-press hover:bg-slate-800">Save scope</button>
                        <button onClick={() => setScopeDraft(null)}
                          className="h-10 px-4 rounded-full border border-slate-200 text-xs font-bold text-slate-500 tap-press">Cancel</button>
                      </div>
                    </div>
                  )}
                </div>
              )}
              <div className="mt-6 space-y-3">
                {active.steps.map((st, i) => (
                  <div key={i} className="flex gap-4 p-4 rounded-xl bg-slate-50 border border-slate-100">
                    <span className="h-8 w-8 shrink-0 rounded-full bg-slate-900 text-white flex items-center justify-center font-heading font-extrabold text-sm">{i + 1}</span>
                    <p className="text-base leading-relaxed pt-1">{st}</p>
                  </div>
                ))}
              </div>
              {active.tags?.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-6">
                  {active.tags.map((t) => <span key={t} className="text-xs font-bold bg-slate-100 text-slate-600 px-3 py-1.5 rounded-full">#{t}</span>)}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showCats && (
        <div className="fixed inset-0 z-[1000] bg-slate-950/50 flex items-center justify-center p-4 sm:p-6" onClick={() => setShowCats(false)}>
          <div data-testid="sop-categories-modal" onClick={(e) => e.stopPropagation()}
            className="bg-white w-full sm:max-w-md max-h-[90vh] overflow-y-auto rounded-3xl p-6 sm:p-8 rise-in space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-2xl font-bold">SOP categories</h2>
              <button onClick={() => setShowCats(false)} className="p-2 rounded-full hover:bg-slate-100 tap-press"><X size={22} /></button>
            </div>
            <div className="flex flex-wrap gap-2">
              {catDraft.map((c) => (
                <span key={c} data-testid={`cat-chip-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="h-9 pl-4 pr-2 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs font-bold flex items-center gap-1">
                  {c}
                  <button onClick={() => setCatDraft(catDraft.filter((x) => x !== c))} className="p-1 hover:text-red-500 tap-press"><X size={12} /></button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input data-testid="cat-add-input" value={newCatName} onChange={(e) => setNewCatName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && newCatName.trim()) { setCatDraft([...catDraft, newCatName.trim()]); setNewCatName(""); } }}
                placeholder="Add category…" className="flex-1 h-11 px-4 rounded-full border border-slate-200 text-sm" />
              <button data-testid="cat-add-button"
                onClick={() => { if (newCatName.trim()) { setCatDraft([...catDraft, newCatName.trim()]); setNewCatName(""); } }}
                className="h-11 px-4 rounded-full bg-slate-900 text-white text-xs font-bold tap-press">Add</button>
            </div>
            <button data-testid="cat-save-button" onClick={saveCats}
              className="w-full py-3.5 rounded-full bg-slate-900 text-white font-heading font-bold tap-press hover:bg-slate-800">
              Save categories
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
